import * as React from 'react';
import styled, {css} from 'styled-components';
import TextareaAutosize from 'react-autosize-textarea';
import {lighten, darken, transparentize} from 'polished';
import InputContainer, {InputContainerProps} from 'containers/InputContainer';
import {transition} from 'helpers/css';
import outline from 'helpers/outline';

export enum TextareaVariant {
  Default = 'default',
  Accent = 'accent'
}
export enum TextareaScale {
  Small = 'small',
  Medium = 'medium',
}

export type TextareaProps =
  & React.InputHTMLAttributes<HTMLTextAreaElement>
  & Omit<InputContainerProps, 'children'>
  & {
  variant?: TextareaVariant;
  scale?: TextareaScale;
  stretched?: boolean;
  rows?: number;
}

export const StyledInput = styled(TextareaAutosize)<TextareaProps>`
  width: ${({stretched}) => stretched && '100%'};
  border: 1px solid ${({error, theme}) => (
    error ? theme.colors.red : 'transparent'
  )};
  box-sizing: border-box;
  
  &::placeholder {
    color: ${({theme}) => theme.variables.placeholderColor};
  }
  
  ${({variant}) => {
    switch (variant) {
      case TextareaVariant.Default: return css`
        background: ${({theme}) => theme.variables.backgroundColor};
        
        &:hover,
        &:focus {
          background: ${({theme}) => `
            ${darken(.01, theme.variables.backgroundColor)}
          `};
        }
      `;

      case TextareaVariant.Accent: return css`
        background: ${({theme}) => theme.variables.accentBackgroundColor};
        
        &:hover,
        &:focus {
          background: ${({theme}) => `
            ${lighten(.02, theme.variables.accentBackgroundColor)}
          `};
        }
        
        &::placeholder {
          color: ${({theme}) => `
            ${transparentize(.7, theme.variables.color)};
          `};
        }
      `;
    }
  }};
  
  ${({scale}) => {
    switch (scale) {
      case TextareaScale.Small: return css`
        padding: 7px 12px;
        border-radius: 5px;
        font-size: 12px;
        line-height: 16px;
      `;
      case TextareaScale.Medium: return css`
        padding: 11px 14px;
        line-height: 20px;
        font-size: 15px;
        border-radius: 5px;
      `;
    }
  }};
    
  &:focus {
    ${outline(0, 1)};
  }
  
  &, &::placeholder {
    ${transition()};
  }
  
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &[type=number] {
    -moz-appearance: textfield;
  }
`;

/**
 * The simple input component
 * @param {TextareaProps} props
 * @constructor
 */
function Textarea(
    props: TextareaProps,
): React.ReactElement<TextareaProps> {
  const inputContainerProps: Omit<InputContainerProps, 'children'> = {
    label: props.label,
    note: props.note,
    error: props.error,
    required: props.required,
  };

  return (
    <InputContainer {...inputContainerProps}>
      <StyledInput
        {...props}
      />
    </InputContainer>
  );
}

Textarea.defaultProps = {
  variant: TextareaVariant.Default,
  scale: TextareaScale.Medium,
};

export default Textarea;
